import { useEffect, useState } from "react";
import { Drawer, Stack, Text, Button, Divider, UnstyledButton, Group, Box } from "@mantine/core";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY_HOME_PATH } from "@/pages/dashboard/dashboardTab.const";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";
import { useLandingNavAuthPaths } from "./landingNavAuthPaths.context";

const HEADER_NAVY = "#04305c";
const HEADER_LINK = "#546E7A";

const pillRadius = { borderRadius: 9999 };

const findCollaborationsStyles = {
  root: {
    ...pillRadius,
    backgroundColor: "#ffffff !important",
    color: `${HEADER_NAVY} !important`,
    fontWeight: 700,
    fontSize: 14,
    border: `1px solid ${HEADER_NAVY}`,
    "&:hover": {
      backgroundColor: "#f8fafc !important",
      color: `${HEADER_NAVY} !important`,
    },
  },
  label: {
    color: `${HEADER_NAVY} !important`,
  },
};

type MobileHamburgerProps = {
  isAuthenticated: boolean;
  clusterProfileHref?: string;
  clusterProfileNavLabel?: string;
};

export default function MobileHamburger({
  isAuthenticated,
  clusterProfileHref,
  clusterProfileNavLabel,
}: MobileHamburgerProps) {
  const [opened, setOpened] = useState(false);
  const location = useLocation();
  const { loginPath, signupPath } = useLandingNavAuthPaths();
  const clusterHref = clusterProfileHref?.trim() || "";
  const clusterLinkLabel = clusterProfileNavLabel?.trim() || "View Cluster";
  const clusterLinkState = clusterHref
    ? { discoverReturnTo: `${location.pathname}${location.search}` }
    : undefined;

  const close = () => setOpened(false);

  useEffect(() => {
    setOpened(false);
  }, [isAuthenticated, location.pathname]);

  const NavItem = ({ to, label, external }: { to: string; label: string; external?: boolean }) =>
    external ? (
      <UnstyledButton component="a" href={to} target="_blank" rel="noopener noreferrer" onClick={close} style={{ padding: "10px 12px", borderRadius: 8 }}>
        <Text fw={700} c={HEADER_LINK}>
          {label}
        </Text>
      </UnstyledButton>
    ) : (
      <UnstyledButton
        component={Link}
        to={to}
        onClick={close}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          background: location.pathname === to ? "#EEF4FF" : "transparent",
        }}
      >
        <Text fw={700} c={location.pathname === to ? "primary.6" : HEADER_LINK}>
          {label}
        </Text>
      </UnstyledButton>
    );

  return (
    <>
      {/*  Hamburger icon */}
      <UnstyledButton onClick={() => setOpened(true)} hiddenFrom="sm" aria-label="Open menu">
        <IconMenu2 size={26} color={HEADER_NAVY} stroke={2} />
      </UnstyledButton>

      {/* Mount only while open so login navigation does not unmount a closed Drawer portal. */}
      {opened ? (
        <Drawer
          opened
          onClose={close}
          padding="lg"
          size="100%"
          position="left"
          withCloseButton={false}
          keepMounted={false}
          portalProps={{ reuseTargetNode: true }}
        >
        {/* Header */}
        <Group justify="space-between" mb="md">
          <Text fw={700} size="lg" c="primary.6">
            GTsetu
          </Text>
          <UnstyledButton onClick={close}>
            <IconX size={22} />
          </UnstyledButton>
        </Group>

        <Stack gap="xs">
          {isAuthenticated ? (
            <>
              {/* Logged-in menu */}
              <NavItem to="/profile" label="My Profile" />
              <NavItem to={MY_CLUSTER_PATH} label="My Clusters" />
              <NavItem to="/" label="Discover" />
              <NavItem to={COMPANY_HOME_PATH} label="Home" />
              <NavItem to="/collaborations" label="Collaborations" />
              <NavItem to="/shortlist" label="Shortlist" />
              <NavItem to="/invoices" label="Invoices" />
              <NavItem to="/support" label="Support" />
            </>
          ) : (
            <>
              {/* Public menu */}
              {clusterHref ? (
                <Button
                  component={Link}
                  to={clusterHref}
                  state={clusterLinkState}
                  fullWidth
                  mb="sm"
                  onClick={close}
                  styles={findCollaborationsStyles}
                >
                  {clusterLinkLabel}
                </Button>
              ) : null}
              <NavItem to="https://gtsetu.com/what-we-do/" label="What We Do?" external />
              <NavItem to="https://gtsetu.com/features/" label="Features" external />
              <NavItem to="https://gtsetu.com/pricing/" label="Pricing" external />
              <NavItem to="https://gtsetu.com/support/" label="Support" external />
              <NavItem to="https://gtsetu.com/resources/" label="Resources" external />
              <NavItem to="https://gtsetu.com/blogs/" label="Blogs" external />
              <NavItem to="https://gtsetu.com/about/" label="About Us" external />
              <NavItem to="https://gtsetu.com/news/" label="News" external />

              <Divider my="md" />

              <Box>
                <Link
                  to={loginPath}
                  onClick={close}
                  className="landing-navbar-auth-btn landing-navbar-login landing-navbar-login--mobile"
                >
                  Login
                </Link>
                <Link
                  to={signupPath}
                  onClick={close}
                  className="landing-navbar-auth-btn landing-navbar-signup landing-navbar-signup--mobile"
                >
                  Signup
                </Link>
              </Box>
            </>
          )}
        </Stack>
        </Drawer>
      ) : null}

    </>
  );
}
